import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState } from "react";
import { apiGet, apiUrl } from "@/shared/api/client";
import { isApiError } from "@/shared/api/errors";
import { invalidateLiveKeys } from "@/shared/api/invalidate";
import { useTenantId } from "@/shared/auth/session";
import {
  LIVE_COALESCE_MS,
  LIVE_PROBE_MS,
  LIVE_REOPEN_MAX_MS,
  LIVE_REOPEN_MS,
} from "@/shared/constants/limits";
import {
  invalidationsFor,
  liveCatchUpKeys,
  LIVE_EVENT_TYPES,
  parseLiveEnvelope,
  type LiveEnvelope,
} from "./liveEvents";

/**
 * off          this browser has no EventSource (jsdom); the polling fallback is all there is
 * connecting   the first attempt is in flight — the screen was just loaded, so its data is current
 * open         messages are arriving; the polling intervals stand down
 * reconnecting between attempts (the api's `retry: 3000`), or waiting to reopen after a refusal
 * limited      429 STREAM_LIMIT: too many tabs. We stop; retrying would only refuse again
 * unavailable  403: this session may not hold a stream at all (a support session, a suspended
 *              organisation). Structural, never transient, so retrying is only noise
 * ended        the session client signed this tab out (a definite 401) or moved it to another
 *              organisation; there is nothing to reopen
 */
export type LiveStatus = "off" | "connecting" | "open" | "reconnecting" | "limited" | "unavailable" | "ended";

/** A page's handler for the raw envelopes of ONE type. */
export type LiveListener = (envelope: LiveEnvelope) => void;

export interface LiveState {
  status: LiveStatus;
  /** True only while messages are actually arriving: the one thing the polling hooks ask. */
  live: boolean;
  /**
   * **M16 wave 4: read the message, not just the fact that one arrived.**
   *
   * Almost every live message says "something changed, refetch", and `invalidationsFor` handles all
   * of them without any page knowing the stream exists. `pass.scan` is the exception the front desk's
   * live view needs: the refusal reason it carries is in no row, so there is nothing to refetch and
   * the envelope itself is the content (ADR-0021 open question 4).
   *
   * This is NOT a licence for a component to open a socket — rules/01 §5 stands, and it stands
   * because of how this works: the shell still owns the ONE EventSource, and a page registers a
   * listener on it. Returns an unsubscribe; `subscribe` is stable across renders, so an effect can
   * depend on it.
   */
  subscribe: (type: string, listener: LiveListener) => () => void;
}

/** Read at connect time, not at module load, so a test can install a fake before the shell mounts. */
function eventSourceCtor(): typeof EventSource | null {
  return typeof window === "undefined" ? null : (window.EventSource ?? null);
}

const CONNECTING = 0;

/**
 * The portal's ONE live channel (rules/01 §5: components never open their own sockets). Mounted once
 * by `LiveProvider` in the app shell; everything else reads `useLiveStatus()`.
 *
 * Cookies ride along on `EventSource` (`withCredentials`), so there is nothing to authenticate here;
 * the organisation pin rides in `?org=` (`apiUrl`), because an `EventSource` cannot send `X-Nacs-Org`.
 * The browser owns the ordinary reconnect — it honours the api's `retry: 3000` and sends
 * `Last-Event-ID`, which replays what was missed — and this hook owns the three cases it will not:
 * a refused connection (the browser closes for good on any non-`text/event-stream` answer, so we ask
 * the route why: 429 STREAM_LIMIT and 403 are structural and stop us, anything else is reopened with
 * a doubling delay), the api's last word on a connection (`renew` / `session`), and the coalescing of
 * invalidations.
 *
 * **The stream never signs anyone out** (ADR-0011 addendum 2026-10-04, plan §5.5). The api closes a
 * connection with `renew` when the access token it opened with expires (every ~15 minutes) and with
 * `session` when that session no longer stands for this organisation. Neither is a verdict: the hook
 * asks the session client with one ordinary call, and the client's own rules decide — renewed, reopen;
 * a blip, "Reconnecting…" and reopen later; moved, the move handler; a definite 401, sign-in.
 */
export function useTenantStream(): LiveState {
  const tenantId = useTenantId();
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<LiveStatus>(() => (eventSourceCtor() ? "connecting" : "off"));

  // Held in a ref so `subscribe` never changes identity and the connection effect below never
  // reconnects because a page mounted.
  const listeners = useRef(new Map<string, Set<LiveListener>>());
  const subscribe = useCallback((type: string, listener: LiveListener) => {
    const set = listeners.current.get(type) ?? new Set<LiveListener>();
    set.add(listener);
    listeners.current.set(type, set);
    return () => {
      set.delete(listener);
      if (set.size === 0) listeners.current.delete(type);
    };
  }, []);

  useEffect(() => {
    const Ctor = eventSourceCtor();
    if (!Ctor) return;

    let source: EventSource | null = null;
    /** The id of the last message delivered on this tenant's stream, for a reopen's replay. */
    let lastEventId: string | null = null;
    /** Opens seen so far; the first one's data is current, because the screen has just loaded it. */
    let opened = 0;
    let stopped = false;
    let checking = false;
    let attempts = 0;
    let flushTimer: ReturnType<typeof setTimeout> | null = null;
    let reopenTimer: ReturnType<typeof setTimeout> | null = null;
    /** Key prefixes waiting for the next flush, deduped by their serialised key. */
    const pending = new Map<string, readonly unknown[]>();

    const flush = (): void => {
      flushTimer = null;
      if (pending.size === 0) return;
      const keys = [...pending.values()];
      pending.clear();
      void invalidateLiveKeys(queryClient, keys);
    };

    const invalidateLater = (keys: readonly (readonly unknown[])[]): void => {
      for (const key of keys) pending.set(JSON.stringify(key), key);
      if (pending.size > 0) flushTimer ??= setTimeout(flush, LIVE_COALESCE_MS);
    };

    const queue = (data: unknown): void => {
      const envelope = parseLiveEnvelope(data);
      if (!envelope) return;
      // Listeners first and synchronously: a scan view must paint the moment the message lands, not
      // after the invalidation debounce. One listener throwing must not cost the invalidations or the
      // connection, so each is isolated.
      for (const listener of listeners.current.get(envelope.type) ?? []) {
        try {
          listener(envelope);
        } catch {
          // A page's own rendering problem is not the stream's to fail over.
        }
      }
      invalidateLater(invalidationsFor(tenantId, envelope));
    };

    const close = (): void => {
      source?.close();
      source = null;
    };

    const reopenLater = (): void => {
      if (stopped || reopenTimer) return;
      const delay = Math.min(LIVE_REOPEN_MS * 2 ** attempts, LIVE_REOPEN_MAX_MS);
      attempts += 1;
      reopenTimer = setTimeout(() => {
        reopenTimer = null;
        connect();
      }, delay);
    };

    /**
     * The session client has already acted on this failure — signed the tab out (a definite 401) or
     * moved it (`ORGANISATION_CHANGED`) — so there is nothing to reopen.
     */
    const settledBySession = (error: unknown): boolean => {
      if (!isApiError(error) || (error.status !== 401 && error.code !== "ORGANISATION_CHANGED")) return false;
      stopped = true;
      setStatus("ended");
      return true;
    };

    /**
     * `renew` or `session`: close, then one ordinary call through the session client. It renews the
     * expired access token (or moves the tab, or signs it out) by its own rules; if the session stands,
     * the stream reopens at once with the new cookie, and a blip reopens it later.
     */
    const recheck = async (): Promise<void> => {
      if (checking) return;
      checking = true;
      close();
      setStatus("reconnecting");
      try {
        await apiGet("/me");
      } catch (error) {
        checking = false;
        if (stopped || settledBySession(error)) return;
        reopenLater();
        return;
      }
      checking = false;
      if (stopped) return;
      attempts = 0;
      connect();
    };

    /**
     * The browser closed the connection for good, which it only does when the answer was not a
     * stream. `EventSource` hands us no status, so we ask the same route with the ordinary client:
     * STREAM_LIMIT is the tab limit and is final, a 401 or a move is the session client's to handle,
     * and a probe that is still streaming when we abort it means the stream is healthy — reopen. A
     * plain 429 (or a renewal that failed for a blip) is transient and reopens later.
     */
    const diagnose = async (): Promise<void> => {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), LIVE_PROBE_MS);
      try {
        await apiGet("/stream", undefined, controller.signal);
      } catch (error) {
        if (stopped) return;
        if (isApiError(error)) {
          if (error.code === "STREAM_LIMIT") {
            stopped = true;
            setStatus("limited");
            return;
          }
          if (settledBySession(error)) return;
          if (error.status === 403) {
            // A support session, or an organisation whose state closes the stream: not transient.
            stopped = true;
            setStatus("unavailable");
            return;
          }
        }
      } finally {
        clearTimeout(timer);
      }
      reopenLater();
    };

    const connect = (): void => {
      if (stopped) return;
      // A connection this hook OPENS AGAIN (after `renew`, `session` or a diagnosis) is a new
      // EventSource, which sends no Last-Event-ID of its own: pass the last id seen, so the api replays
      // what arrived in the gap instead of leaving counts and sync states stale (review 2026-10-04).
      const es = new Ctor(apiUrl("/stream", lastEventId ? { lastEventId } : undefined), {
        withCredentials: true,
      });
      source = es;
      es.onopen = () => {
        attempts = 0;
        // Open again with no id to replay from: a quiet tab's renewal, or the browser's own reconnect
        // after a drop (it sends Last-Event-ID only once a message has arrived). The api replays
        // nothing to a fresh connection, so refetch what the stream keeps current, once, instead of
        // leaving counts and sync states stale (review 2026-10-04).
        if (opened > 0 && !lastEventId) invalidateLater(liveCatchUpKeys(tenantId));
        opened += 1;
        setStatus("open");
      };
      for (const type of LIVE_EVENT_TYPES) {
        es.addEventListener(type, (event) => {
          const message = event as MessageEvent<string>;
          if (message.lastEventId) lastEventId = message.lastEventId;
          queue(message.data);
        });
      }
      // The api's last word on a connection: its access token expired (`renew`), or the session no
      // longer stands for this organisation (`session`). Both go through the session client.
      es.addEventListener("renew", () => void recheck());
      es.addEventListener("session", () => void recheck());
      es.onerror = () => {
        if (stopped) return;
        if (es.readyState === CONNECTING) {
          // A dropped connection: the browser is already retrying with the api's `retry` interval.
          setStatus("reconnecting");
          return;
        }
        close();
        setStatus("reconnecting");
        void diagnose();
      };
    };

    connect();

    return () => {
      stopped = true;
      if (flushTimer) clearTimeout(flushTimer);
      if (reopenTimer) clearTimeout(reopenTimer);
      close();
    };
  }, [tenantId, queryClient]);

  return { status, live: status === "open", subscribe };
}
